import {
  ForwardedRef,
  forwardRef,
  FunctionComponentElement,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { useFeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import {
  AddOrEditNotiRuleModalScreens,
  AvailableScreenProps,
  NotificationRuleDialogContentHandle,
  NotificationRuleDialogContentProps,
  NotificationRuleSubmitForm,
} from '$features/notifications/types';

import {
  ConditionScreen,
  DevicesScreen,
  DeviceTypeScreen,
  ErrorDialog,
  LifecycleScreen,
  NameScreen,
  RecipientsScreen,
} from './screens';

function NotificationRuleDialogContentComponent(
  {
    handleFormSubmit,
    isLoading,
    requestError,
    formMethods,
    initialScreen: initialScreenProp,
    onScreenChange,
  }: NotificationRuleDialogContentProps,
  ref: ForwardedRef<NotificationRuleDialogContentHandle>,
): FunctionComponentElement<NotificationRuleDialogContentProps> {
  const [screenHasErrors, setScreenHasErrors] = useState<boolean>(true);
  const isNotificationTypesEnabled = useFeatureIsEnabled(
    'ESEVNLS_4596_NOTIFICATION_TYPES',
  );

  const notificationRuleScreens: Record<
    AddOrEditNotiRuleModalScreens,
    AvailableScreenProps
  > = {
    deviceTypeScreen: {
      key: 'deviceTypeScreen',
      fields: ['selectedDeviceType'],
    },
    devicesScreen: {
      key: 'devicesScreen',
      fields: ['devices'],
    },
    conditionScreen: {
      key: 'conditionScreen',
      fields: ['tags', 'categories'],
    },
    recipientsScreen: {
      key: 'recipientsScreen',
      fields: ['escalationLayers'],
    },
    lifecycleScreen: {
      key: 'lifecycleScreen',
      fields: ['notificationTypes'],
    },
    nameScreen: {
      key: 'nameScreen',
      fields: ['notificationRuleName'],
    },
    successScreen: {
      key: 'successScreen',
      fields: [],
    },
    errorScreen: {
      key: 'errorScreen',
      fields: [],
    },
  };

  const availableScreens = Object.entries(notificationRuleScreens).filter(
    ([key]) => isNotificationTypesEnabled || key !== 'lifecycleScreen',
  );

  const initialScreen =
    initialScreenProp &&
    (isNotificationTypesEnabled || initialScreenProp !== 'lifecycleScreen')
      ? // eslint-disable-next-line security/detect-object-injection -- internal
        notificationRuleScreens[initialScreenProp]
      : notificationRuleScreens.deviceTypeScreen;

  const [activeScreen, setActiveScreen] = useState<AvailableScreenProps>(initialScreen);
  const [screenBeforeError, setScreenBeforeError] =
    useState<AvailableScreenProps>(initialScreen);

  useEffect(() => {
    onScreenChange?.(activeScreen.key);
  }, [activeScreen]);

  function getNextScreen(): AvailableScreenProps | false {
    const currentStepIndex = availableScreens.findIndex(
      ([key]) => key === activeScreen.key,
    );

    // If current step is the last step, return false
    if (currentStepIndex === availableScreens.length - 1) {
      return false;
    }

    // Return the next step
    const next = availableScreens[currentStepIndex + 1];
    return { ...next[1] };
  }

  const getPrevScreen = (): AvailableScreenProps | false => {
    const currentStepIndex = availableScreens.findIndex(
      ([key]) => key === activeScreen.key,
    );

    // If current step is the first step, return false
    if (currentStepIndex === 0) {
      return false;
    }

    // Return the previous step
    const prev = availableScreens[currentStepIndex - 1];
    return { ...prev[1] };
  };

  const handlePrevious = () => {
    const prevScreen = getPrevScreen();
    if (prevScreen) setActiveScreen(prevScreen);
  };

  const handleTryAgain = () => {
    setActiveScreen(screenBeforeError);
  };

  const handleNext = () => {
    const nextScreen = getNextScreen();
    if (nextScreen) setActiveScreen(nextScreen);
  };

  useImperativeHandle(
    ref,
    (): NotificationRuleDialogContentHandle => ({
      goToScreen: (screen: AddOrEditNotiRuleModalScreens) => {
        const currentStepIndex = availableScreens.findIndex(
          ([key]) => key === activeScreen.key,
        );
        const targetStepIndex = availableScreens.findIndex(([key]) => key === screen);

        // Only allow navigating back to a step that was already executed
        if (targetStepIndex === -1 || targetStepIndex >= currentStepIndex) return;

        // eslint-disable-next-line security/detect-object-injection -- targetStepIndex is a bounded array index
        const target = availableScreens[targetStepIndex];
        setActiveScreen({ ...target[1] });
      },
    }),
    [activeScreen, availableScreens],
  );

  // Check if screen has errors
  useEffect(() => {
    setScreenHasErrors(
      Object.keys(formMethods.formState.errors).some((key) =>
        activeScreen.fields.includes(key as keyof NotificationRuleSubmitForm),
      ),
    );
  }, [formMethods.formState, activeScreen]);

  useEffect(() => {
    const requestErrorStatus = requestError?.response?.status;
    if (requestError && requestErrorStatus !== 409) {
      setScreenBeforeError(activeScreen);
      setActiveScreen(notificationRuleScreens.errorScreen);
    }
  }, [requestError]);

  return (
    <form onSubmit={formMethods.handleSubmit(handleFormSubmit)}>
      {activeScreen.key === 'deviceTypeScreen' && (
        <DeviceTypeScreen
          formControl={formMethods}
          onContinue={handleNext}
          isLoading={isLoading}
          screenHasErrors={screenHasErrors}
        />
      )}

      {activeScreen.key === 'devicesScreen' && (
        <DevicesScreen
          formControl={formMethods}
          onBack={handlePrevious}
          onContinue={handleNext}
          screenHasErrors={screenHasErrors}
        />
      )}

      {activeScreen.key === 'conditionScreen' && (
        <ConditionScreen
          formControl={formMethods}
          onBack={handlePrevious}
          onContinue={handleNext}
          screenHasErrors={screenHasErrors}
        />
      )}

      {activeScreen.key === 'recipientsScreen' && (
        <RecipientsScreen
          formControl={formMethods}
          onBack={handlePrevious}
          onContinue={handleNext}
          isLoading={isLoading}
          screenHasErrors={screenHasErrors}
        />
      )}

      {isNotificationTypesEnabled && activeScreen.key === 'lifecycleScreen' && (
        <LifecycleScreen
          formControl={formMethods}
          onBack={handlePrevious}
          onContinue={handleNext}
          screenHasErrors={screenHasErrors}
        />
      )}

      {activeScreen.key === 'nameScreen' && (
        <NameScreen
          formControl={formMethods}
          onBack={handlePrevious}
          isLoading={isLoading}
          screenHasErrors={screenHasErrors}
        />
      )}

      {activeScreen.key === 'errorScreen' && <ErrorDialog onTryAgain={handleTryAgain} />}
    </form>
  );
}

export const NotificationRuleDialogContent = forwardRef(
  NotificationRuleDialogContentComponent,
);
